import { ChangeDetectorRef, Component, EventEmitter, Input, Output } from "@angular/core";
import { Table } from "../Table";
import { S25ButtonComponent } from "../../../standalone/s25.button.component";

export interface TableAction {
    label: string;
    type?: S25ButtonComponent["type"];
    disabled?: boolean;
    disabledReason?: string;
    onClick?: (row?: Table.Row, instance?: GenericTableActionsComponent) => Promise<void> | void;
    visible?: boolean; // If false, button won't be rendered
}

@Component({
    selector: "s25-ng-generic-table-actions",
    template: `
        <div class="btn-group">
            @for (action of visibleActions; track $index) {
                <s25-ng-button
                    [type]="action.type || 'outline'"
                    [disabled]="action.disabled"
                    [disabledReason]="action.disabledReason"
                    [isLoading]="loadingStates[$index]"
                    (click)="handleClick(action, $index); $event?.stopPropagation()"
                    (keydown.enter)="$event?.stopPropagation()"
                >
                    {{ action.label }}
                </s25-ng-button>
            }
        </div>
    `,
    styles: [
        `
            :host {
                display: block;
            }
            .btn-group {
                display: flex;
                gap: 0.5rem;
            }
        `,
    ],
    standalone: false,
})
export class GenericTableActionsComponent {
    @Input() actions: TableAction[] = [];
    @Output() actionClick = new EventEmitter<{ action: TableAction; index: number }>();

    loadingStates: boolean[] = [];

    constructor(private changeDetector: ChangeDetectorRef) {}

    get visibleActions(): TableAction[] {
        return this.actions.filter((action) => action.visible !== false);
    }

    async handleClick(action: TableAction, index: number): Promise<void> {
        if (action.disabled || this.loadingStates[index]) {
            return;
        }

        this.actionClick.emit({ action, index });

        if (action.onClick) {
            this.loadingStates[index] = true;
            this.changeDetector.detectChanges();

            try {
                await action.onClick();
            } finally {
                this.loadingStates[index] = false;
                this.changeDetector.detectChanges();
            }
        }
    }

    /**
     * Static helper method to create a column configuration for table actions
     */
    public static Column(
        getActions: (row: Table.Row) => TableAction[],
        width: Table.Column["width"] = "min-content",
        customHeader: string = "Actions",
    ): Table.Column {
        let currentRow: Table.Row;
        let currentInstance: GenericTableActionsComponent;

        return {
            id: "actions",
            header: customHeader,
            width: width,
            align: "center",
            sortable: false,
            cells: {
                component: GenericTableActionsComponent,
                inputs: {
                    actions: [], // Will be set dynamically per row
                },
                outputs: {
                    actionClick: (
                        event: { action: TableAction; index: number },
                        context: Table.CellCallbackContext,
                    ) => {
                        const { row, instance } = context;
                        currentRow = row;
                        currentInstance = instance;

                        // Update actions with row context
                        const actions = getActions(row);
                        actions.forEach((action) => {
                            const originalOnClick = action.onClick;
                            if (originalOnClick) {
                                action.onClick = () => originalOnClick(row, instance);
                            }
                        });
                        instance.actions = actions;
                        instance.changeDetector.detectChanges();
                    },
                },
            },
        };
    }
}
